/*eslint-disable max-lines*/
import { Camera, CameraGroupCamera } from '@hakimo-ui/hakimo/types';
import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import { InputField } from '@hakimo-ui/shared/ui-base';
import { useMemo } from 'react';
import DraggableCameraItem from './DraggableCameraItem';

interface Props {
  locationId: string;
  search: string;
  setSearch: (v: string) => void;
  dragCam: CameraGroupCamera | null;
  cameraMap: Map<string, Camera>;
  ungroupedCameras: CameraGroupCamera[];
  groupedCameras: CameraGroupCamera[];
  isLoading?: boolean;
}

function LocationCameraCard({
  locationId,
  search,
  setSearch,
  dragCam,
  cameraMap,
  ungroupedCameras,
  groupedCameras,
  isLoading = false,
}: Props) {
  const filteredUngroupedCameras = useMemo(() => {
    if (!search) return ungroupedCameras;
    return ungroupedCameras.filter((cam) =>
      cam.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [ungroupedCameras, search]);

  const filteredGroupedCameras = useMemo(() => {
    if (!search) return groupedCameras;
    return groupedCameras.filter((cam) =>
      cam.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [groupedCameras, search]);
  const showSelectLocationMessage = !locationId;
  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 flex max-h-[36rem] flex-col rounded-xl p-4">
      <InputField
        type="search"
        value={search}
        placeholder="Search cameras"
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="mt-4 flex-1 overflow-y-auto">
        {showSelectLocationMessage && (
          <div className="flex items-center justify-center">
            <p className="text-onlight-text-3 px-4 py-36 text-center text-sm">
              Please select a location to view cameras
            </p>
          </div>
        )}
        {!showSelectLocationMessage && isLoading && (
          <div className="flex h-full items-center justify-center py-24">
            <LoadingIndicator />
          </div>
        )}
        {!showSelectLocationMessage && !isLoading && (
          <div className="space-y-4">
            <div>
              <p className="pl-2 font-medium">Ungrouped Cameras</p>
              {filteredUngroupedCameras.length === 0 && (
                <div className="text-onlight-text-3 flex px-2 pt-2 text-sm">
                  No cameras
                </div>
              )}
              {filteredUngroupedCameras.map((cam) => (
                <DraggableCameraItem
                  key={cam.id}
                  cam={cam}
                  id={cam.id}
                  name={cam.name}
                  imageUrl={cameraMap.get(cam.id)?.imageUrl}
                  livestreamUrl={cameraMap.get(cam.id)?.livestreamUrl}
                  draggable
                  showDragIcon
                  isDragging={dragCam?.id === cam.id}
                />
              ))}
            </div>

            <div>
              <p className="pl-2 font-medium">Grouped Cameras</p>
              {filteredGroupedCameras.map((cam) => (
                <DraggableCameraItem
                  key={cam.id}
                  cam={cam}
                  id={cam.id}
                  name={cam.name}
                  imageUrl={cameraMap.get(cam.id)?.imageUrl}
                  livestreamUrl={cameraMap.get(cam.id)?.livestreamUrl}
                  draggable
                  showDragIcon
                  isDragging={dragCam?.id === cam.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationCameraCard;
